/**
 * PrivateOffersTab — Super-Admin-only management surface for private
 * subscription grants. Lets the admin search verified users, grant
 * them a plan with discount + duration + notes, toggle active state,
 * edit, and delete. Uses:
 *   - GET    /api/admin/users?q=…             → user search
 *   - GET    /api/admin/private-offers         → list all offers
 *   - POST   /api/admin/private-offers         → create
 *   - PATCH  /api/admin/private-offers/{id}    → update / deactivate
 *   - DELETE /api/admin/private-offers/{id}    → delete
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Gift, Plus, Search, RefreshCw, Loader2, Trash2, Edit3, X, Check, User as UserIcon,
  Percent, CalendarDays, Power, PowerOff, Ban, ShieldCheck,
} from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { apiClient, formatApiError } from "../../lib/api";

const PLANS = [
  { id: "individual_plus", ar: "الأفراد بلس", en: "Individual Plus" },
  { id: "dealer_pro",       ar: "التاجر برو",  en: "Dealer Pro" },
  { id: "enterprise",       ar: "المؤسسات",    en: "Enterprise" },
];

function planLabel(id, lang) {
  const p = PLANS.find((x) => x.id === id);
  return p ? (lang === "ar" ? p.ar : p.en) : id;
}

function statusLabel(o, lang) {
  const now = new Date();
  const end = o.end_at ? new Date(o.end_at) : null;
  if (o.reverted_at) return lang === "ar" ? "منتهي" : "Expired/Reverted";
  if (o.activated_at) return lang === "ar" ? "مفعّل" : "Activated";
  if (!o.active) return lang === "ar" ? "معطّل" : "Inactive";
  if (end && end < now) return lang === "ar" ? "انتهت الصلاحية" : "Expired";
  return lang === "ar" ? "بانتظار التفعيل" : "Pending activation";
}

function statusColor(o) {
  if (o.reverted_at) return "#8a8a8a";
  if (o.activated_at) return "#0f7c3a";
  if (!o.active) return "#b91c1c";
  const end = o.end_at ? new Date(o.end_at) : null;
  if (end && end < new Date()) return "#b91c1c";
  return "#a8893e";
}

export default function PrivateOffersTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formSeed, setFormSeed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/private-offers", { params: { q: q || undefined } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setFormSeed(null); setShowForm(true); };
  const openEdit = (o) => { setEditingId(o.id); setFormSeed(o); setShowForm(true); };

  const remove = async (o) => {
    if (!window.confirm(lang === "ar" ? "حذف هذا العرض نهائياً؟" : "Delete this offer permanently?")) return;
    try {
      await apiClient.delete(`/admin/private-offers/${o.id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  const toggleActive = async (o) => {
    try {
      await apiClient.patch(`/admin/private-offers/${o.id}`, { active: !o.active });
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="admin-private-offers-tab">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-[#a8893e]" />
          <h2 className="font-display text-2xl">
            {lang === "ar" ? "العروض الخاصة" : "Private Offers"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute start-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
            <input
              className="input-dark ps-8"
              placeholder={lang === "ar" ? "بحث بالبريد أو الملاحظات…" : "Search email or notes…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              data-testid="private-offers-search"
            />
          </div>
          <button onClick={load} className="btn-ghost text-sm" data-testid="private-offers-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> {lang === "ar" ? "تحديث" : "Refresh"}
          </button>
          <button onClick={openCreate} className="btn-primary text-sm" data-testid="private-offers-new">
            <Plus className="w-4 h-4" /> {lang === "ar" ? "منح عرض" : "Grant offer"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#a8893e]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[#8a8a8a]" data-testid="private-offers-empty">
          {lang === "ar" ? "لا توجد عروض خاصة بعد." : "No private offers yet."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#0a0a0a]/[0.08]">
          <table className="w-full text-sm">
            <thead className="bg-[#fafaf7] text-[#525252]">
              <tr>
                <th className="text-start p-3">{lang === "ar" ? "المستخدم" : "User"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الباقة" : "Plan"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الخصم" : "Discount"}</th>
                <th className="text-start p-3">{lang === "ar" ? "البداية" : "Start"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الانتهاء" : "End"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                <th className="text-start p-3">{lang === "ar" ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="border-t border-[#0a0a0a]/[0.06]" data-testid={`private-offer-row-${o.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-[#0a0a0a]">{o.user_name || "—"}</div>
                    <div className="text-[11px] text-[#8a8a8a]">{o.user_email}</div>
                  </td>
                  <td className="p-3">{planLabel(o.plan, lang)}</td>
                  <td className="p-3">{o.discount_pct}%</td>
                  <td className="p-3 text-[12px] text-[#525252]">{o.start_at ? o.start_at.slice(0, 10) : "—"}</td>
                  <td className="p-3 text-[12px] text-[#525252]">{o.end_at ? o.end_at.slice(0, 10) : "—"}</td>
                  <td className="p-3">
                    <span
                      className="inline-block px-2 py-1 rounded-full text-[11px] font-medium"
                      style={{ background: `${statusColor(o)}18`, color: statusColor(o) }}
                    >
                      {statusLabel(o, lang)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleActive(o)}
                        className="p-1.5 rounded hover:bg-[#0a0a0a]/5"
                        title={o.active ? (lang === "ar" ? "تعطيل" : "Deactivate") : (lang === "ar" ? "تفعيل" : "Activate")}
                        data-testid={`toggle-${o.id}`}
                      >
                        {o.active
                          ? <PowerOff className="w-4 h-4 text-[#b91c1c]" />
                          : <Power className="w-4 h-4 text-[#0f7c3a]" />}
                      </button>
                      <button
                        onClick={() => openEdit(o)}
                        className="p-1.5 rounded hover:bg-[#0a0a0a]/5"
                        title={lang === "ar" ? "تعديل" : "Edit"}
                        data-testid={`edit-${o.id}`}
                      >
                        <Edit3 className="w-4 h-4 text-[#525252]" />
                      </button>
                      <button
                        onClick={() => remove(o)}
                        className="p-1.5 rounded hover:bg-red-50"
                        title={lang === "ar" ? "حذف" : "Delete"}
                        data-testid={`delete-${o.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-[#b91c1c]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <OfferForm
          lang={lang}
          seed={formSeed}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

// ── OfferForm modal ────────────────────────────────────────────────
function OfferForm({ lang, seed, onClose, onSaved }) {
  const isEdit = !!seed;
  const [userQ, setUserQ] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(
    seed ? { id: seed.user_id, email: seed.user_email, name: seed.user_name, verified: true } : null,
  );
  const [plan, setPlan] = useState(seed?.plan || "individual_plus");
  const [discount, setDiscount] = useState(seed?.discount_pct ?? 100);
  const [startAt, setStartAt] = useState(seed?.start_at ? seed.start_at.slice(0, 10) : "");
  const [endAt, setEndAt] = useState(seed?.end_at ? seed.end_at.slice(0, 10) : "");
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState(seed?.internal_notes || "");
  const [active, setActive] = useState(seed?.active ?? true);
  const [busy, setBusy] = useState(false);

  const canSave = useMemo(() => {
    if (busy) return false;
    if (!selectedUser) return false;
    if (discount < 0 || discount > 100) return false;
    if (!isEdit && !endAt && !duration) return false;
    return true;
  }, [busy, selectedUser, discount, endAt, duration, isEdit]);

  // Debounced user search (verified users only).
  useEffect(() => {
    if (isEdit) return;
    if (!userQ || userQ.length < 2) { setUserResults([]); return; }
    const t = setTimeout(async () => {
      setUserSearching(true);
      try {
        const { data } = await apiClient.get("/admin/users", { params: { q: userQ, limit: 20 } });
        // Filter to verified accounts only (any verification signal).
        const verifiedOnly = (data.items || []).filter(
          (u) => u.verified === true || u.verification_status === "verified",
        );
        setUserResults(verifiedOnly);
      } catch (e) {
        // ignore — keep list empty
      } finally {
        setUserSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [userQ, isEdit]);

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      if (isEdit) {
        const patch = {
          plan, discount_pct: Number(discount),
          active,
          internal_notes: notes,
        };
        if (startAt) patch.start_at = new Date(startAt + "T00:00:00Z").toISOString();
        if (endAt) patch.end_at = new Date(endAt + "T23:59:59Z").toISOString();
        await apiClient.patch(`/admin/private-offers/${seed.id}`, patch);
        toast.success(lang === "ar" ? "تم تحديث العرض" : "Offer updated");
      } else {
        const body = {
          user_id: selectedUser.id,
          plan,
          discount_pct: Number(discount),
          active,
          internal_notes: notes,
        };
        if (endAt) {
          body.end_at = new Date(endAt + "T23:59:59Z").toISOString();
          if (startAt) body.start_at = new Date(startAt + "T00:00:00Z").toISOString();
        } else {
          body.duration_days = Number(duration);
          if (startAt) body.start_at = new Date(startAt + "T00:00:00Z").toISOString();
        }
        await apiClient.post("/admin/private-offers", body);
        toast.success(lang === "ar" ? "تم إنشاء العرض" : "Offer created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || (lang === "ar" ? "تعذّر الحفظ" : "Failed to save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="offer-form-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#0a0a0a]/[0.08]">
          <h3 className="font-display text-xl flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#a8893e]" />
            {isEdit
              ? (lang === "ar" ? "تعديل عرض خاص" : "Edit private offer")
              : (lang === "ar" ? "منح عرض خاص" : "Grant private offer")}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#0a0a0a]/5" data-testid="offer-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* User picker */}
          <div>
            <label className="block text-[12px] font-semibold text-[#404040] mb-1 flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5" />
              {lang === "ar" ? "الحساب المستهدف" : "Target account"}
            </label>
            {selectedUser ? (
              <div className="flex items-center justify-between p-3 rounded-lg border border-[#a8893e]/25 bg-[#fbf7ec]">
                <div>
                  <div className="font-medium text-[13px]">{selectedUser.name || selectedUser.email}</div>
                  <div className="text-[11px] text-[#8a8a8a] flex items-center gap-1">
                    {selectedUser.email}
                    <ShieldCheck className="w-3 h-3 text-[#0f7c3a]" />
                  </div>
                </div>
                {!isEdit && (
                  <button
                    onClick={() => { setSelectedUser(null); setUserQ(""); setUserResults([]); }}
                    className="text-[12px] text-[#b91c1c]"
                    data-testid="clear-user"
                  >
                    {lang === "ar" ? "تغيير" : "Change"}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute start-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
                  <input
                    className="input-dark ps-8"
                    placeholder={lang === "ar" ? "ابحث بالبريد أو الاسم…" : "Search by email or name…"}
                    value={userQ}
                    onChange={(e) => setUserQ(e.target.value)}
                    data-testid="user-search-input"
                    autoFocus
                  />
                </div>
                {userQ.length >= 2 && (
                  <div className="mt-2 border border-[#0a0a0a]/[0.08] rounded-lg max-h-56 overflow-y-auto">
                    {userSearching ? (
                      <div className="p-3 text-center text-[#8a8a8a]"><Loader2 className="w-4 h-4 animate-spin inline-block" /></div>
                    ) : userResults.length === 0 ? (
                      <div className="p-3 text-center text-[12px] text-[#8a8a8a] flex items-center justify-center gap-1">
                        <Ban className="w-3.5 h-3.5" />
                        {lang === "ar" ? "لا توجد حسابات موثّقة مطابقة" : "No verified accounts match"}
                      </div>
                    ) : userResults.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => { setSelectedUser(u); setUserResults([]); }}
                        className="w-full text-start p-3 hover:bg-[#fafaf7] border-b last:border-b-0 border-[#0a0a0a]/[0.04]"
                        data-testid={`user-pick-${u.id}`}
                      >
                        <div className="font-medium text-[13px]">{u.name || u.email}</div>
                        <div className="text-[11px] text-[#8a8a8a] flex items-center gap-1">
                          {u.email}
                          <ShieldCheck className="w-3 h-3 text-[#0f7c3a]" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Plan + discount */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-[#404040] mb-1">
                {lang === "ar" ? "الباقة" : "Plan"}
              </label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                className="input-dark"
                data-testid="plan-select"
              >
                {PLANS.map((p) => (
                  <option key={p.id} value={p.id}>{lang === "ar" ? p.ar : p.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#404040] mb-1 flex items-center gap-1">
                <Percent className="w-3.5 h-3.5" />
                {lang === "ar" ? "نسبة الخصم" : "Discount %"}
              </label>
              <input
                type="number" min={0} max={100}
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="input-dark"
                data-testid="discount-input"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-[#404040] mb-1 flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" />
                {lang === "ar" ? "تاريخ البدء (اختياري)" : "Start date (optional)"}
              </label>
              <input
                type="date"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className="input-dark"
                data-testid="start-at-input"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#404040] mb-1 flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" />
                {lang === "ar" ? "تاريخ الانتهاء" : "End date"}
              </label>
              <input
                type="date"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className="input-dark"
                data-testid="end-at-input"
              />
            </div>
            {!isEdit && !endAt && (
              <div>
                <label className="block text-[12px] font-semibold text-[#404040] mb-1">
                  {lang === "ar" ? "أو المدة (أيام)" : "Or duration (days)"}
                </label>
                <input
                  type="number" min={1} max={3650}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="input-dark"
                  data-testid="duration-input"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[12px] font-semibold text-[#404040] mb-1">
              {lang === "ar" ? "ملاحظات داخلية (اختياري)" : "Internal notes (optional)"}
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-dark"
              placeholder={lang === "ar" ? "لا تظهر للمستخدم — للتوثيق الداخلي فقط" : "Not shown to the user — internal audit trail"}
              data-testid="notes-input"
            />
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="active-toggle"
            />
            {lang === "ar" ? "العرض مفعّل ومتاح للمستخدم للتفعيل" : "Offer is active and available to the user"}
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#0a0a0a]/[0.08]">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="offer-form-cancel">
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="btn-primary text-sm"
            data-testid="offer-form-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isEdit
              ? (lang === "ar" ? "حفظ التعديلات" : "Save changes")
              : (lang === "ar" ? "منح العرض" : "Grant offer")}
          </button>
        </div>
      </div>
    </div>
  );
}
